import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { fmtDateTime, num } from "@/lib/format";
import { usePermissions, downloadReport, CATEGORY_STYLE } from "@/lib/smartInventory";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ScanSearch, Search, Download, GitMerge, Undo2, ShieldAlert, CheckCircle2, AlertTriangle } from "lucide-react";

const CATEGORIES = [
  ["EXATA", "Duplicidade exata"], ["ALTA", "Alta probabilidade"],
  ["CONFLITANTE", "Referência conflitante"], ["POSSIVEL", "Possível duplicidade"],
];
const TABS = [["grupos", "Duplicidades"], ["confirmadas", "Aguardando consolidação"],
  ["consolidadas", "Consolidados"], ["incompletos", "Cadastros incompletos"]];
const FIELDS = [
  ["codigo_interno", "Código"], ["codigo_omie", "Cód. Omie"], ["descricao", "Descrição"],
  ["fabricante", "Fabricante"], ["codigo_fornecedor", "Ref. fornecedor"], ["unidade", "Unidade"],
  ["quantidade_atual", "Saldo"], ["localizacao", "Localização"], ["status", "Situação"],
];

function CategoryBadge({ value, label }) {
  return <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${CATEGORY_STYLE[value] || ""}`}>{label}</span>;
}

function ExportMenu({ tipo }) {
  return (
    <div className="flex flex-wrap gap-1">
      {["xlsx", "csv", "pdf"].map((f) => (
        <Button key={f} size="sm" variant="outline" className="border-slate-600 bg-slate-900 text-slate-200"
          onClick={() => downloadReport("/part-audit/report", { tipo, formato: f }, tipo === "incompletos" ? "cadastros-incompletos" : "auditoria-duplicidades")}>
          <Download className="h-3.5 w-3.5 mr-1" />{f === "xlsx" ? "Excel" : f.toUpperCase()}
        </Button>
      ))}
    </div>
  );
}

function ReasonDialog({ open, title, text, confirmLabel, danger, onClose, onConfirm, busy }) {
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) setReason(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm text-slate-300">{text}</div>
        <div><Label htmlFor="audit-reason">Motivo (obrigatório)</Label>
          <Input id="audit-reason" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} className="bg-slate-950 border-slate-700 mt-1" /></div>
        <DialogFooter>
          <Button variant="ghost" disabled={busy} onClick={onClose}>Voltar</Button>
          <Button disabled={busy || !reason.trim()} onClick={() => onConfirm(reason.trim())}
            className={danger ? "bg-rose-700 text-white" : "bg-sky-700 text-white"}>{busy ? "Salvando..." : confirmLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReviewDialog({ group, onClose, onDone, canReview }) {
  const [principal, setPrincipal] = useState(null);
  const [equals, setEquals] = useState([]);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!group) return;
    const active = group.pecas.filter((p) => p.status !== "INATIVO");
    const best = [...active].sort((a, b) => (b.quantidade_atual || 0) - (a.quantidade_atual || 0))[0];
    setPrincipal(best?.id || null);
    setEquals(group.categoria === "CONFLITANTE" ? [] : active.filter((p) => p.id !== best?.id).map((p) => p.id));
    setReason("");
  }, [group]);
  if (!group) return null;
  const toggle = (id) => setEquals((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  const send = async (acao) => {
    setBusy(true);
    try {
      const body = acao === "CONFIRMAR" ? { acao, principal_id: principal, iguais: equals.filter((e) => e !== principal), motivo: reason } : { acao, motivo: reason };
      await api.post(`/part-audit/groups/${group.id}/decision`, body);
      toast.success(acao === "CONFIRMAR" ? "Equivalência confirmada. Falta consolidar os cadastros." : acao === "DIFERENTES" ? "Marcadas como peças diferentes." : "Sugestão ignorada.");
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const pending = group.status === "PENDENTE";
  return (
    <Dialog open={!!group} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-5xl w-[calc(100vw-1.5rem)] max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex flex-wrap items-center gap-2">
          Revisar grupo <CategoryBadge value={group.categoria} label={group.categoria_label} />
          <span className="text-sm font-mono text-slate-400">{group.score}% de similaridade</span>
        </DialogTitle></DialogHeader>
        {group.alterado_desde_analise && <p className="text-sm text-amber-300">Algum cadastro mudou desde a análise. Confira os dados atuais abaixo.</p>}
        <div className="overflow-x-auto rounded-lg border border-slate-800">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-950 text-xs text-slate-400">
              <th className="px-3 py-2 text-left w-32"> </th>
              {group.pecas.map((p) => (
                <th key={p.id} className="px-3 py-2 text-left min-w-[180px]">
                  <label className="flex items-center gap-2 text-slate-200 font-semibold">
                    <input type="radio" name="principal" disabled={!pending || p.status === "INATIVO"} checked={principal === p.id} onChange={() => setPrincipal(p.id)} />
                    Principal
                  </label>
                  {principal !== p.id && <label className="mt-1 flex items-center gap-2 text-slate-300">
                    <input type="checkbox" disabled={!pending} checked={equals.includes(p.id)} onChange={() => toggle(p.id)} />
                    Igual ao principal
                  </label>}
                </th>
              ))}
            </tr></thead>
            <tbody>
              {FIELDS.map(([key, label]) => (
                <tr key={key} className="border-t border-slate-800">
                  <td className="px-3 py-2 text-xs uppercase text-slate-500">{label}</td>
                  {group.pecas.map((p) => (
                    <td key={p.id} className={`px-3 py-2 ${key === "descricao" ? "text-slate-100" : "text-slate-300"} ${key === "quantidade_atual" ? "font-mono" : ""}`}>
                      {key === "codigo_interno" ? <Link className="text-sky-300 underline" to={`/pecas/${p.id}`} target="_blank">{p[key] || "—"}</Link> : (p[key] ?? "—")}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="border-t border-slate-800">
                <td className="px-3 py-2 text-xs uppercase text-slate-500">Especificações</td>
                {group.pecas.map((p) => (
                  <td key={p.id} className="px-3 py-2 text-xs text-slate-300">
                    {Object.entries(p.atributos || {}).filter(([k]) => !k.endsWith("_txt")).map(([k, v]) => <div key={k}>{k}: {Array.isArray(v) ? v.map((x) => Array.isArray(x) ? x.join("×") : x).join(", ") : String(v)}</div>)}
                    {(p.atributos?.potencia_txt || []).length > 0 && <div>potência: {p.atributos.potencia_txt.join(", ")}</div>}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        <div className="space-y-2">
          {group.pares.map((pair) => {
            const a = group.pecas.find((p) => p.id === pair.a); const b = group.pecas.find((p) => p.id === pair.b);
            return (
              <div key={pair.key} className="rounded-lg border border-slate-800 bg-slate-950/60 p-3 text-sm">
                <div className="font-mono text-slate-300">{a?.codigo_interno} × {b?.codigo_interno}: <b className="text-slate-100">{pair.score}%</b> (texto {pair.texto}%)</div>
                {pair.motivos.length > 0 && <div className="mt-1 text-emerald-300/90">✓ {pair.motivos.join(" · ")}</div>}
                {pair.conflitos.length > 0 && <div className="mt-1 text-rose-300">⚠ {pair.conflitos.join(" · ")}</div>}
              </div>
            );
          })}
        </div>
        <p className="text-xs text-slate-400">Semelhança não é prova. Confirme só se for a mesma peça física. Confirmar não mexe no estoque nem apaga nada: a consolidação é feita depois, por um administrador.</p>
        {pending && canReview ? <>
          <div><Label htmlFor="review-reason">Observação (opcional)</Label>
            <Input id="review-reason" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} className="bg-slate-950 border-slate-700 mt-1" /></div>
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="ghost" disabled={busy} onClick={() => send("IGNORAR")}>Ignorar sugestão</Button>
            <Button variant="outline" disabled={busy} onClick={() => send("DIFERENTES")} className="border-slate-500 bg-slate-800 text-slate-100">São peças diferentes</Button>
            <Button disabled={busy || !principal || equals.filter((e) => e !== principal).length === 0} onClick={() => send("CONFIRMAR")} className="bg-emerald-700 hover:bg-emerald-600 text-white">
              <CheckCircle2 className="h-4 w-4 mr-1" />Confirmar iguais
            </Button>
          </DialogFooter>
        </> : <p className="text-sm text-slate-300">Situação: {group.status === "PENDENTE" ? "pendente" : `revisado (${group.decisao || ""}) por ${group.resolvido_por_nome || "—"} em ${fmtDateTime(group.resolvido_em)}`}</p>}
      </DialogContent>
    </Dialog>
  );
}

function EquivalenceCard({ eq, action, onAction }) {
  const parts = [eq.principal, ...eq.pecas].filter(Boolean);
  const total = parts.reduce((s, p) => s + (p.quantidade_atual || 0), 0);
  return (
    <Card className="card-metal p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-slate-400">Confirmado por {eq.confirmado_por_nome || "—"} em {fmtDateTime(eq.confirmado_em)}
          {eq.consolidado_em && <> · consolidado por {eq.consolidado_por_nome} em {fmtDateTime(eq.consolidado_em)}</>}</div>
        {action && <Button size="sm" onClick={() => onAction(eq)} className={action === "undo" ? "bg-slate-700 text-white" : "bg-sky-700 text-white"}>
          {action === "undo" ? <><Undo2 className="h-4 w-4 mr-1" />Desfazer</> : <><GitMerge className="h-4 w-4 mr-1" />Consolidar</>}
        </Button>}
      </div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {parts.map((p, i) => (
          <div key={p.id} className={`rounded-lg border p-3 text-sm ${i === 0 ? "border-sky-700 bg-sky-950/30" : "border-slate-800 bg-slate-950/50"}`}>
            <div className="text-xs uppercase text-slate-500">{i === 0 ? "Cadastro principal" : "Duplicado"}</div>
            <Link to={`/pecas/${p.id}`} className="font-mono text-sky-300 underline">{p.codigo_interno || "—"}</Link>
            <div className="text-slate-100">{p.descricao}</div>
            <div className="text-xs text-slate-400">Saldo: <span className="font-mono">{num(p.quantidade_atual)}</span>{p.localizacao ? ` · ${p.localizacao}` : ""}</div>
          </div>
        ))}
      </div>
      {action === "consolidate" && total > 0 && parts.length > 1 &&
        <p className="text-xs text-amber-300">Os saldos não serão somados. Faça um inventário: a peça é contada uma vez e o ajuste vai para o cadastro principal.</p>}
    </Card>
  );
}

export default function PartAudit() {
  const can = usePermissions();
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("grupos");
  const [categoria, setCategoria] = useState("TODAS");
  const [status, setStatus] = useState("PENDENTE");
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  const [page, setPage] = useState(1);
  const [groups, setGroups] = useState(null);
  const [review, setReview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [eqs, setEqs] = useState(null);
  const [incomplete, setIncomplete] = useState(null);
  const [eqAction, setEqAction] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadSummary = useCallback(() => api.get("/part-audit/summary").then((r) => setSummary(r.data)).catch((e) => { toast.error(apiErr(e)); setSummary({}); }), []);
  const loadGroups = useCallback(() => {
    setGroups(null);
    api.get("/part-audit/groups", { params: { categoria: categoria === "TODAS" ? undefined : categoria, status, q: term || undefined, page } })
      .then((r) => setGroups(r.data)).catch((e) => { toast.error(apiErr(e)); setGroups({ items: [], total_pages: 1 }); });
  }, [categoria, status, term, page]);
  const loadEqs = useCallback(() => {
    setEqs(null);
    const st = tab === "confirmadas" ? "CONFIRMADA,CONSOLIDANDO" : "CONSOLIDADA";
    api.get("/part-audit/equivalences", { params: { status: st } }).then((r) => setEqs(r.data)).catch(() => setEqs([]));
  }, [tab]);
  const loadIncomplete = useCallback(() => {
    setIncomplete(null);
    api.get("/part-audit/incomplete", { params: { q: term || undefined, page } }).then((r) => setIncomplete(r.data)).catch(() => setIncomplete({ items: [] }));
  }, [term, page]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { const t = setTimeout(() => { setPage(1); setTerm(q); }, 350); return () => clearTimeout(t); }, [q]);
  useEffect(() => {
    if (tab === "grupos") loadGroups();
    else if (tab === "incompletos") loadIncomplete();
    else loadEqs();
  }, [tab, loadGroups, loadIncomplete, loadEqs]);

  const scan = async () => {
    setScanning(true);
    try { const r = await api.post("/part-audit/scan"); setSummary(r.data); toast.success("Análise concluída."); setPage(1); loadGroups(); }
    catch (e) { toast.error(apiErr(e)); } finally { setScanning(false); }
  };
  const doEqAction = async (reason) => {
    setBusy(true);
    try {
      await api.post(`/part-audit/equivalences/${eqAction.eq.id}/${eqAction.kind === "undo" ? "undo" : "consolidate"}`, { motivo: reason });
      toast.success(eqAction.kind === "undo" ? "Consolidação desfeita. Os cadastros voltaram como estavam." : "Cadastros consolidados. Os códigos antigos continuam encontrando a peça.");
      setEqAction(null); loadEqs(); loadSummary();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const cats = summary?.categorias || {};
  const run = summary?.ultima_analise;
  const labels = summary?.labels || {};

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-50">Auditoria Inteligente de Peças</h1>
          <p className="text-slate-400 text-sm max-w-2xl">Encontra cadastros repetidos ou incompletos. Nada é mesclado ou apagado sozinho: cada sugestão passa por você.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {can("auditoria.revisar") && <Button onClick={scan} disabled={scanning} className="bg-sky-700 hover:bg-sky-600" data-testid="btn-audit-scan">
            <ScanSearch className="h-4 w-4 mr-1" />{scanning ? "Analisando..." : run ? "Analisar de novo" : "Analisar cadastros"}
          </Button>}
          {can("relatorios.ver") && <ExportMenu tipo={tab === "incompletos" ? "incompletos" : "duplicidades"} />}
        </div>
      </div>

      {summary === null ? <Skeleton className="h-24 bg-slate-800" /> : (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
          {CATEGORIES.map(([key, label]) => (
            <button key={key} type="button" onClick={() => { setTab("grupos"); setCategoria(key); setStatus("PENDENTE"); setPage(1); }}
              className={`rounded-xl border p-3 text-left ${CATEGORY_STYLE[key]}`}>
              <div className="text-2xl font-bold font-mono">{num(cats[key]?.PENDENTE || 0)}</div>
              <div className="text-xs">{label}</div>
              {(cats[key]?.RESOLVIDO || cats[key]?.CONFIRMADO) ? <div className="text-[11px] opacity-70">{(cats[key]?.RESOLVIDO || 0) + (cats[key]?.CONFIRMADO || 0)} revisados</div> : null}
            </button>
          ))}
          <div className="rounded-xl border border-sky-800 bg-sky-950/40 p-3 text-sky-200"><div className="text-2xl font-bold font-mono">{num(summary.equivalencias?.confirmadas || 0)}</div><div className="text-xs">Aguardando consolidação</div></div>
          <div className="rounded-xl border border-emerald-800 bg-emerald-950/40 p-3 text-emerald-200"><div className="text-2xl font-bold font-mono">{num(summary.equivalencias?.consolidadas || 0)}</div><div className="text-xs">Grupos consolidados</div></div>
          <div className="rounded-xl border border-slate-700 bg-slate-900 p-3 text-slate-200"><div className="text-2xl font-bold font-mono">{summary.sem_suspeita == null ? "—" : num(summary.sem_suspeita)}</div><div className="text-xs">Sem suspeita de duplicidade</div></div>
        </div>
      )}
      {run ? <p className="text-xs text-slate-500">Última análise: {fmtDateTime(run.created_at)} por {run.usuario_nome || "—"} · {num(run.total_pecas)} peças analisadas.</p>
        : summary && <p className="text-sm text-amber-300">Nenhuma análise feita ainda. Clique em “Analisar cadastros”.</p>}

      <div className="flex flex-wrap gap-2" role="tablist">
        {TABS.map(([key, label]) => (
          <Button key={key} role="tab" aria-selected={tab === key} variant={tab === key ? "default" : "outline"} onClick={() => { setTab(key); setPage(1); }}
            className={tab === key ? "bg-sky-800 text-white" : "border-slate-700 bg-slate-900 text-slate-300"}>{label}</Button>
        ))}
      </div>

      {(tab === "grupos" || tab === "incompletos") && (
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[220px]"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por descrição, código ou referência" className="pl-9 bg-slate-950 border-slate-700" /></div>
          {tab === "grupos" && <>
            <Select value={categoria} onValueChange={(v) => { setCategoria(v); setPage(1); }}>
              <SelectTrigger className="w-[220px] bg-slate-950 border-slate-700"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                <SelectItem value="TODAS">Todas as classificações</SelectItem>
                {CATEGORIES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
              <SelectTrigger className="w-[160px] bg-slate-950 border-slate-700"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                <SelectItem value="PENDENTE">Pendentes</SelectItem><SelectItem value="RESOLVIDO">Resolvidos</SelectItem><SelectItem value="TODOS">Todos</SelectItem>
              </SelectContent>
            </Select>
          </>}
        </div>
      )}

      {tab === "grupos" && (groups === null ? <Skeleton className="h-64 bg-slate-800" /> : (
        <div className="space-y-3">
          {groups.items.length === 0 && <div className="py-12 text-center text-slate-500"><ShieldAlert className="mx-auto mb-2 h-10 w-10 opacity-50" />Nenhum grupo nesta seleção.</div>}
          {groups.items.map((g) => (
            <Card key={g.id} className="card-metal p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <CategoryBadge value={g.categoria} label={labels[g.categoria] || g.categoria} />
                  <span className="font-mono text-sm text-slate-300">{g.score}%</span>
                  <span className="text-xs text-slate-500">{g.membros.length} cadastros</span>
                  {g.status !== "PENDENTE" && <span className="rounded bg-slate-800 px-2 py-0.5 text-xs text-slate-300">{g.status === "CONFIRMADO" ? "Confirmado como igual" : g.decisao === "IGNORAR" ? "Ignorado" : "Diferentes"}</span>}
                  {g.alterado_desde_analise && <span className="flex items-center gap-1 text-xs text-amber-300"><AlertTriangle className="h-3.5 w-3.5" />mudou desde a análise</span>}
                </div>
                <div className="flex gap-2">
                  {g.status !== "PENDENTE" && can("auditoria.revisar") && <Button size="sm" variant="ghost" onClick={async () => {
                    try { await api.post(`/part-audit/groups/${g.id}/reopen`, {}); toast.success("Revisão reaberta."); loadGroups(); loadSummary(); } catch (e) { toast.error(apiErr(e)); }
                  }}>Reabrir</Button>}
                  <Button size="sm" onClick={() => setReview({ ...g, categoria_label: labels[g.categoria] })} className="bg-sky-800 text-white">{g.status === "PENDENTE" && can("auditoria.revisar") ? "Revisar" : "Ver"}</Button>
                </div>
              </div>
              <div className="mt-3 grid gap-1 text-sm">
                {g.pecas.map((p) => (
                  <div key={p.id} className="flex flex-wrap gap-x-3 text-slate-300">
                    <span className="font-mono text-sky-300 w-24 shrink-0">{p.codigo_interno || "—"}</span>
                    <span className="text-slate-100">{p.descricao}</span>
                    <span className="text-xs text-slate-500">saldo {num(p.quantidade_atual)}{p.fabricante ? ` · ${p.fabricante}` : ""}{p.status === "INATIVO" ? " · inativa" : ""}</span>
                  </div>
                ))}
              </div>
              {g.pares[0]?.conflitos?.length > 0 && <p className="mt-2 text-xs text-rose-300">⚠ {g.pares[0].conflitos.join(" · ")}</p>}
            </Card>
          ))}
          <Pager page={page} total={groups.total_pages} onPage={setPage} />
        </div>
      ))}

      {(tab === "confirmadas" || tab === "consolidadas") && (eqs === null ? <Skeleton className="h-48 bg-slate-800" /> : (
        <div className="space-y-3">
          {tab === "confirmadas" && !can("auditoria.consolidar") && eqs.length > 0 && <p className="text-sm text-slate-400">Um administrador precisa consolidar estes cadastros.</p>}
          {eqs.length === 0 && <div className="py-12 text-center text-slate-500">Nada por aqui.</div>}
          {eqs.map((eq) => <EquivalenceCard key={eq.id} eq={eq} action={can("auditoria.consolidar") ? (tab === "confirmadas" ? "consolidate" : "undo") : null}
            onAction={(item) => setEqAction({ eq: item, kind: tab === "confirmadas" ? "consolidate" : "undo" })} />)}
        </div>
      ))}

      {tab === "incompletos" && (incomplete === null ? <Skeleton className="h-48 bg-slate-800" /> : (
        <Card className="card-metal p-0 overflow-hidden">
          <div className="flex flex-wrap gap-2 p-3 text-xs text-slate-400">
            {Object.entries(incomplete.por_pendencia || {}).map(([k, v]) => <span key={k} className="rounded bg-slate-800 px-2 py-0.5">{k}: {num(v)}</span>)}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-950 text-xs uppercase text-slate-400"><th className="px-3 py-2 text-left">Código</th><th className="px-3 py-2 text-left">Descrição</th><th className="px-3 py-2 text-right">Saldo</th><th className="px-3 py-2 text-left">Pendências</th></tr></thead>
              <tbody>{incomplete.items.map((r) => (
                <tr key={r.id} className="border-t border-slate-800">
                  <td className="px-3 py-2 font-mono"><Link to={`/pecas/${r.id}`} className="text-sky-300 underline">{r.codigo_interno || "—"}</Link></td>
                  <td className="px-3 py-2 text-slate-200">{r.descricao}</td>
                  <td className="px-3 py-2 text-right font-mono text-slate-300">{num(r.quantidade_atual)}</td>
                  <td className="px-3 py-2 text-xs text-amber-300">{r.pendencias.join(" · ")}</td>
                </tr>))}</tbody>
            </table>
          </div>
          <div className="p-3"><Pager page={page} total={incomplete.total_pages} onPage={setPage} /></div>
        </Card>
      ))}

      <ReviewDialog group={review} canReview={can("auditoria.revisar")} onClose={() => setReview(null)} onDone={() => { setReview(null); loadGroups(); loadSummary(); }} />
      <ReasonDialog open={!!eqAction} busy={busy} onClose={() => setEqAction(null)} onConfirm={doEqAction}
        danger={eqAction?.kind === "undo"}
        title={eqAction?.kind === "undo" ? "Desfazer consolidação?" : "Consolidar cadastros?"}
        confirmLabel={eqAction?.kind === "undo" ? "Desfazer" : "Consolidar"}
        text={eqAction?.kind === "undo"
          ? <p>Os cadastros duplicados voltam a ficar como estavam antes. O estoque não muda.</p>
          : <><p>Os duplicados ficam inativos e passam a apontar para o cadastro principal. Os códigos antigos continuam encontrando a peça.</p>
            <p>Nada é apagado: movimentações, inventários e dados fiscais continuam no histórico. Os saldos não são somados.</p></>} />
    </div>
  );
}

function Pager({ page, total, onPage }) {
  if (!total || total <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-3 text-sm text-slate-300">
      <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPage(page - 1)} className="border-slate-700 bg-slate-900">Anterior</Button>
      <span>Página {page} de {total}</span>
      <Button size="sm" variant="outline" disabled={page >= total} onClick={() => onPage(page + 1)} className="border-slate-700 bg-slate-900">Próxima</Button>
    </div>
  );
}
